import { Pencil, Trash2, Copy } from "lucide-react";
import { Link } from "react-router-dom";

const TYPE_STYLES = {
  flat_rate: "border-l-2 border-amber-400",
  unit_based: "border-l-2 border-teal-400",
};

const TYPE_BADGE = {
  flat_rate: { label: "Flat", cls: "bg-amber-100 text-amber-700" },
  unit_based: { label: "Unit", cls: "bg-teal-100 text-teal-700" },
};

const JOB_STATUS_COLORS = {
  Draft:                "bg-gray-100 text-gray-600",
  "Offered to Vendor":  "bg-blue-100 text-blue-700",
  "Offer Accepted":     "bg-indigo-100 text-indigo-700",
  "Offer Rejected":     "bg-red-100 text-red-700",
  Started:              "bg-yellow-100 text-yellow-700",
  Completed:            "bg-green-100 text-green-700",
  "Completion Accepted":"bg-emerald-100 text-emerald-700",
  "Completion Rejected":"bg-rose-100 text-rose-700",
  Hold:                 "bg-orange-100 text-orange-700",
  Cancelled:            "bg-gray-200 text-gray-500",
};

const PayablesTable = ({
  data = [],
  onEdit,
  onDelete,
  onClone,
  selectedIds = [],
  onToggle,
  onToggleAll,
}) => {
  const fmt = (v, fallback = "—") =>
    v !== undefined && v !== null && v !== "" ? v : fallback;

  const fmtNum = (v, fallback = "0.00") =>
    v !== undefined && v !== null && v !== "" ? v : fallback;

  const formatDate = (d) => {
    if (!d) return "—";
    try { return new Date(d).toLocaleDateString(); } catch { return "—"; }
  };

  const formatLanguagePair = (lp) => {
    if (!lp) return "—";
    const src = lp.sourceLanguage?.name || lp.sourceLanguage?.code || "";
    const tgt = lp.targetLanguage?.name || lp.targetLanguage?.code || "";
    return src && tgt ? `${src} → ${tgt}` : "—";
  };

  const formatCurrency = (c) => c?.currency?.code || "—";

  const getVendorName = (vendor) => {
    if (!vendor) return "—";
    if (vendor.type === "Company") return vendor.company_name || "—";
    const u = vendor.primary_users;
    return u ? `${u.first_name} ${u.last_name}`.trim() : "—";
  };

  const eligible = data.filter((r) => !r.vendorInvoice && r.job?.status === "Completion Accepted");
  const allSelected = eligible.length > 0 && eligible.every((r) => selectedIds.includes(`${r.type}-${r.id}`));
  const someSelected = data.some((r) => selectedIds.includes(`${r.type}-${r.id}`));

  const HEADERS = [
    "checkbox",
    "Type",
    "Code",
    "Invoice",
    "Job Vendor",
    "Job Service",
    "Job Language Pair",
    "Unit Amount",
    "Unit",
    "Price / Unit",
    "Total",
    "Currency",
    "Job",
    "Job Deadline",
    "Job Status",
    "Filename",
    "Note for Vendor",
    "Internal Note",
    "Actions",
  ];

  const COL_WIDTHS = [
    "2%",   // checkbox
    "3%",   // Type
    "4%",   // Code
    "5%",   // Invoice
    "7%",   // Job Vendor
    "7%",   // Job Service
    "9%",   // Job Language Pair
    "3%",   // Unit Amount
    "3%",   // Unit
    "4%",   // Price / Unit
    "4%",   // Total
    "4%",   // Currency
    "6%",   // Job
    "6%",   // Job Deadline
    "7%",   // Job Status
    "7%",   // Filename
    "6%",   // Note for Vendor
    "5%",   // Internal Note
    "7%",   // Actions
  ];

  return (
    <div className="w-full bg-white shadow rounded-lg overflow-hidden">
      <div className="flex items-center gap-4 px-4 py-2 border-b bg-gray-50 text-xs text-gray-500">
        <span className="flex items-center gap-1">
          <span className="inline-block w-2 h-3 rounded-sm bg-amber-400" />
          Flat Rate
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-2 h-3 rounded-sm bg-teal-400" />
          Unit Based
        </span>
        {selectedIds.length > 0 && (
          <span className="ml-auto text-blue-600 font-medium">
            {selectedIds.length} selected
          </span>
        )}
      </div>

      <div className="max-h-[600px] overflow-y-auto overflow-x-auto">
        <table className="w-full table-auto border-collapse min-w-[1800px]">
          <colgroup>
            {COL_WIDTHS.map((w, i) => <col key={i} style={{ width: w }} />)}
          </colgroup>

          <thead className="sticky top-0 z-10">
            <tr className="bg-white border-b text-[10px] tracking-widest text-gray-500 uppercase">
              <th className="px-3 py-2 text-center">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(el) => { if (el) el.indeterminate = someSelected && !allSelected; }}
                  onChange={() => onToggleAll?.(eligible)}
                  className="w-3.5 h-3.5 accent-blue-600 cursor-pointer"
                />
              </th>
              {HEADERS.slice(1).map((h) => (
                <th key={h} className="px-3 py-2 text-center font-semibold whitespace-nowrap">
                  {h}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {data.length === 0 ? (
              <tr>
                <td colSpan={HEADERS.length} className="py-10 text-center text-gray-400 text-xs">
                  No payables found.
                </td>
              </tr>
            ) : (
              data.map((row, index) => {
                const rowKey = `${row.type}-${row.id}`;
                const isInvoiced = !!row.vendorInvoice;
                const jobAccepted = row.job?.status === "Completion Accepted";
                const canInvoice = !isInvoiced && jobAccepted;
                const isSelected = selectedIds.includes(rowKey);
                const isFlat = row.type === "flat_rate";
                const badge = TYPE_BADGE[row.type] ?? TYPE_BADGE.flat_rate;
                const typeStyle = TYPE_STYLES[row.type] ?? "";
                const rowBg = !canInvoice && !isSelected
                  ? "bg-gray-100 opacity-70"
                  : isSelected
                  ? "bg-blue-50"
                  : index % 2 === 0
                  ? "bg-gray-50"
                  : "bg-white";
                const jobStatusCls = JOB_STATUS_COLORS[row.job?.status] ?? "bg-gray-100 text-gray-600";

                return (
                  <tr
                    key={rowKey}
                    className={`${rowBg} ${typeStyle} hover:bg-blue-50 transition-colors`}
                  >
                    <td className="px-3 py-2 text-center">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        disabled={!canInvoice}
                        onChange={() => canInvoice && onToggle?.(rowKey)}
                        className="w-3.5 h-3.5 accent-blue-600 cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
                        title={isInvoiced ? "Already invoiced" : !jobAccepted ? "Job must be Completion Accepted to invoice" : undefined}
                      />
                    </td>

                    <td className="px-3 py-2 text-center whitespace-nowrap">
                      <span className={`inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded ${badge.cls}`}>
                        {badge.label}
                      </span>
                    </td>

                    <td className="px-3 py-2 text-xs whitespace-nowrap">
                      <Link
                        to={`/vendor/payable/${row.id}?type=${row.type}`}
                        className="text-blue-600 hover:text-blue-800 underline font-medium"
                      >
                        APY{row.id}
                      </Link>
                    </td>

                    <td className="px-3 py-2 text-xs whitespace-nowrap text-center">
                      {row.vendorInvoice ? (
                        <Link
                          to={`/vendor/invoice/${row.vendorInvoice.id}`}
                          className="text-blue-600 hover:underline font-medium"
                        >
                          {row.vendorInvoice.invoice_code}
                        </Link>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>

                    <td className="px-3 py-2 text-xs break-words">
                      {row.job?.vendor?.id ? (
                        <Link
                          to={`/vendors/${row.job.vendor.id}`}
                          className="text-blue-600 hover:text-blue-800 underline font-medium"
                        >
                          {getVendorName(row.job.vendor)}
                        </Link>
                      ) : (
                        getVendorName(row.job?.vendor)
                      )}
                    </td>

                    <td className="px-3 py-2 text-xs break-words">
                      {row.job?.service?.name || "—"}
                    </td>

                    <td className="px-3 py-2 text-xs break-words">
                      {formatLanguagePair(row.job?.languagePair)}
                    </td>

                    <td className="px-3 py-2 text-xs text-right whitespace-nowrap">
                      {isFlat ? <span className="text-gray-300">—</span> : fmt(row.unit_amount)}
                    </td>

                    <td className="px-3 py-2 text-xs whitespace-nowrap">
                      {isFlat ? <span className="text-gray-300">—</span> : (row.unit?.name || "—")}
                    </td>

                    <td className="px-3 py-2 text-xs text-right whitespace-nowrap">
                      {isFlat ? <span className="text-gray-300">—</span> : fmt(row.price_per_unit)}
                    </td>

                    <td className="px-3 py-2 text-xs font-semibold text-right whitespace-nowrap">
                      {fmtNum(row.subtotal)}
                    </td>

                    <td className="px-3 py-2 text-xs whitespace-nowrap">
                      {formatCurrency(row.currency)}
                    </td>

                    <td className="px-3 py-2 text-xs whitespace-nowrap">
                      {row.job ? (
                        <Link
                          to={`/project/${row.project_id}/job/${row.job_id}`}
                          className="text-blue-600 hover:text-blue-800 underline font-medium"
                        >
                          {row.job.name}
                        </Link>
                      ) : "—"}
                    </td>

                    <td className="px-3 py-2 text-xs whitespace-nowrap">
                      {formatDate(row.job?.deadline_at)}
                    </td>

                    <td className="px-3 py-2 text-center whitespace-nowrap">
                      {row.job?.status ? (
                        <span className={`inline-block text-[10px] font-semibold px-2 py-0.5 rounded ${jobStatusCls}`}>
                          {row.job.status}
                        </span>
                      ) : "—"}
                    </td>

                    <td className="px-3 py-2 text-xs break-words">
                      {row.file?.file_name || row.file?.file_code || "—"}
                    </td>

                    <td className="px-3 py-2 text-xs text-gray-600 break-words">
                      {fmt(row.note_for_vendor)}
                    </td>

                    <td className="px-3 py-2 text-xs text-gray-600 break-words">
                      {fmt(row.internal_note)}
                    </td>

                    <td className="px-3 py-2 text-center whitespace-nowrap">
                      <div className="flex justify-center gap-3">
                        <Copy
                          className="w-4 h-4 text-gray-400 hover:text-gray-700 cursor-pointer transition-colors"
                          title="Clone"
                          onClick={() => onClone?.(row)}
                        />
                        <Pencil
                          className={`w-4 h-4 transition-colors ${isInvoiced ? "text-gray-200 cursor-not-allowed" : "text-blue-400 hover:text-blue-600 cursor-pointer"}`}
                          title={isInvoiced ? "Cannot edit an invoiced payable" : "Edit"}
                          onClick={() => !isInvoiced && onEdit?.(row)}
                        />
                        <Trash2
                          className={`w-4 h-4 transition-colors ${isInvoiced ? "text-gray-200 cursor-not-allowed" : "text-red-400 hover:text-red-600 cursor-pointer"}`}
                          title={isInvoiced ? "Cannot delete an invoiced payable" : "Delete"}
                          onClick={() => !isInvoiced && onDelete?.(row)}
                        />

                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PayablesTable;
